// Why a GPU fleet dies and a general-purpose fleet does not: fungibility.
//
// Dependency-free on purpose: features/writing/articles.ts globs /content/writing/*.mdx
// with `eager: true`, so anything an article imports ships in the MAIN client bundle to
// every visitor. Same constraint the harness diagram documents. Plain markup + Tailwind,
// no SVG coordinates to maintain, and it stacks on narrow screens for free.
//
// TWO ROWS, DELIBERATELY. This started as a taxonomy of places to run a model (hosted
// API / serverless / rented / on-prem) and then as a whose-hardware x CPU/GPU matrix.
// Both were doing the prose's job. The figure has exactly one thing to show — the
// mechanism Fly's post-mortem describes, that segregated capacity cannot be backfilled
// — and every extra row made that harder to see, not easier. The catalogue of options
// belongs in the article body; this is the comparison the argument turns on.

type Fleet = {
	name: string
	shape: string
	idle: string
	outcome: string
	fading?: boolean
}

const FLEETS: Fleet[] = [
	{
		name: 'Segregated GPU fleet',
		shape: 'A second, smaller pool only GPU work may enter',
		idle: 'Few customers, bursty demand, so headroom is large relative to the pool — and every idle unit costs roughly an order of magnitude more than an ordinary server.',
		outcome: 'Closing',
		fading: true,
	},
	{
		name: 'Mixed general-purpose fleet',
		shape: 'One large pool any workload can enter',
		idle: 'Independent customers peak at different moments, so bursts cancel and the headroom needed grows far slower than the fleet.',
		outcome: 'Fine, and always has been',
	},
]

export function InferencePlacement() {
	return (
		<figure className="not-prose my-8">
			<ul className="space-y-3">
				{FLEETS.map((f) => (
					<li
						key={f.name}
						className={[
							'rounded-lg border p-4',
							f.fading
								? 'border-dashed border-neutral-400 dark:border-neutral-600'
								: 'border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900',
						].join(' ')}
					>
						<div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
							<p
								className={[
									'text-sm font-semibold',
									f.fading
										? 'text-neutral-500 dark:text-neutral-400'
										: 'text-neutral-900 dark:text-neutral-100',
								].join(' ')}
							>
								{f.name}
							</p>
							<p className="text-xs tracking-wide text-neutral-500 uppercase">
								{f.outcome}
							</p>
						</div>
						<p className="mt-1 text-xs text-neutral-500">{f.shape}</p>
						<p className="mt-2 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">
							{f.idle}
						</p>
					</li>
				))}
			</ul>
			<figcaption className="mt-3 text-xs text-neutral-500">
				Pooling is the whole story. Splitting one well-behaved pool into a big
				cheap one and a small expensive one puts all the volatility in the most
				expensive place.
			</figcaption>
		</figure>
	)
}
